import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import postService, { Post, UpdatePostData } from '../../services/postService';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
// import CustomDatePicker from '../ui/CustomDatePicker';
import { useToast } from '@/hooks/use-toast';
import { useClient } from '../../contexts/ClientContext';
import { Clock } from 'lucide-react';

interface SchedulePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
  onScheduleSuccess: (updatedPost: Post) => void;
}

const SchedulePostModal: React.FC<SchedulePostModalProps> = ({ isOpen, onClose, post, onScheduleSuccess }) => {
  const { currentClient } = useClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [scheduledAt, setScheduledAt] = useState<string | undefined>(undefined);

  // Initialize with existing scheduled time if available
  useEffect(() => {
    if (post.scheduled_at) {
      // Convert UTC time to local time for datetime-local input
      const utcDate = new Date(post.scheduled_at);
      const localDateTime = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setScheduledAt(localDateTime);
    }
  }, [post.scheduled_at]);

  const updateScheduleMutation = useMutation({
    mutationFn: ({ postId, data }: { postId: string; data: UpdatePostData }) => 
      postService.updatePost(postId, data),
    onSuccess: (data) => {
      toast({
        title: "Post Scheduled",
        description: "Your post has been scheduled successfully.",
      });
      
      // Invalidate all relevant queries to ensure calendar updates
      queryClient.invalidateQueries({ queryKey: ['scheduledPosts', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduledPostGroups', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduledPostsForQueue'] });
      queryClient.invalidateQueries({ queryKey: ['draftPosts'] });
      queryClient.invalidateQueries({ queryKey: ['postGroups'] });
      
      if (onScheduleSuccess) {
        onScheduleSuccess(data);
      }
      onClose();
    },
    onError: () => {
      toast({
        title: "Scheduling Failed",
        description: "Failed to schedule the post. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSchedule = () => {
    if (!scheduledAt) {
      toast({
        title: "Date Required",
        description: "Please select a date and time for scheduling.",
        variant: "destructive",
      });
      return;
    }

    const updatedData: UpdatePostData = {
      scheduled_at: new Date(scheduledAt).toISOString(),
      status: 'scheduled',
    };
    updateScheduleMutation.mutate({ postId: post.id, data: updatedData });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-slate-900 border-slate-200/50 dark:border-slate-600/30 w-full max-w-md backdrop-blur-xl">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <span>Schedule Post</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-xl bg-slate-50/50 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-200/50 dark:border-slate-600/30">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5"></div>
            <div className="relative p-4">
              <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-200 mb-2">
                {post.title}
              </h4>
              <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                {post.content || 'No content available.'}
              </p>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-sm border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5"></div>
            <div className="relative p-4">
              <label className="block text-sm font-semibold text-slate-900 dark:text-slate-200 mb-3">
                Schedule Date & Time
              </label>
              <input
                type="datetime-local"
                value={scheduledAt || ''}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full px-3 py-2 bg-slate-100/50 dark:bg-slate-700/50 border border-slate-200/50 dark:border-slate-600/30 rounded-lg text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 text-sm"
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4 border-t border-slate-200/50 dark:border-slate-600/30">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="bg-slate-100/50 hover:bg-slate-200/50 dark:bg-slate-700/50 border-slate-300/70 dark:border-slate-600/50 dark:hover:bg-slate-600/50 text-slate-700 dark:text-slate-300"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSchedule}
            disabled={updateScheduleMutation.isPending}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
          >
            {updateScheduleMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                Scheduling...
              </>
            ) : (
              <>
                <Clock className="w-4 h-4 mr-2" />
                Schedule Post
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SchedulePostModal;
